import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, ShoppingCart, Clock, Music } from 'lucide-react';
import axios from 'axios';
import { usePlayer } from '../context/PlayerContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function AlbumDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTrack, isPlaying, playTrack, pause } = usePlayer();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [album, setAlbum] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/albums/${id}`);
        setAlbum(response.data.album);
        setTracks(response.data.tracks);
      } catch (error) {
        logger.error('Failed to fetch album:', error);
        navigate('/music');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handlePlayTrack = (track) => {
    if (currentTrack?.id === track.id && isPlaying) {
      pause();
    } else {
      playTrack(track, tracks);
    }
  };

  const handlePlayAll = () => {
    if (tracks.length > 0) {
      playTrack(tracks[0], tracks);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    setAddingToCart(true);
    try {
      await addToCart(album.id, 'album');
      toast.success('Album added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const totalDuration = tracks.reduce((acc, t) => acc + t.duration, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!album) return null;

  return (
    <div className="main-content pt-24" data-testid="album-detail-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          <div className="w-full md:w-72 flex-shrink-0">
            <img 
              src={album.cover_url} 
              alt={album.title}
              className="w-full aspect-square rounded-2xl object-cover shadow-2xl"
            />
          </div>
          
          <div className="flex flex-col justify-end">
            <p className="text-sm text-purple-400 font-medium mb-2">ALBUM</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{album.title}</h1>
            <p className="text-gray-400 mb-4 max-w-lg">{album.description}</p>
            
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
              <span className="font-medium text-white">{album.artist}</span>
              <span>•</span>
              <span>{album.release_date}</span>
              <span>•</span>
              <span>{tracks.length} tracks</span>
              <span>•</span>
              <span>{formatDuration(totalDuration)}</span>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={handlePlayAll}
                className="btn-primary flex items-center gap-2"
                data-testid="play-album"
              >
                <Play className="w-5 h-5" />
                Play All
              </button>
              <button
                onClick={handleAddToCart}
                disabled={addingToCart}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-album-to-cart"
              >
                <ShoppingCart className="w-5 h-5" />
                Buy Album ${album.price.toFixed(2)}
              </button>
            </div>
          </div>
        </div>

        {/* Track List */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-semibold">Tracks</h2>
          </div>
          
          <div className="divide-y divide-white/5">
            {tracks.map((track, index) => {
              const isCurrentTrackPlaying = currentTrack?.id === track.id;
              
              return (
                <div 
                  key={track.id}
                  className="track-item hover:bg-white/5 cursor-pointer"
                  onClick={() => handlePlayTrack(track)}
                  data-testid={`album-track-${track.id}`}
                >
                  <div className="w-8 flex justify-center">
                    {isCurrentTrackPlaying && isPlaying ? (
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                      </div>
                    ) : (
                      <span className="text-gray-500 group-hover:hidden">{index + 1}</span>
                    )}
                  </div>
                  
                  <img 
                    src={track.cover_url} 
                    alt={track.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                  
                  <div className="min-w-0">
                    <p className={`font-medium truncate ${isCurrentTrackPlaying ? 'text-purple-400' : ''}`}>
                      {track.title}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{track.artist}</p>
                  </div>
                  
                  <span className="text-sm text-gray-500 hidden md:block">
                    {formatDuration(track.duration)}
                  </span>
                  
                  <span className="text-sm text-purple-400 hidden md:block">
                    ${track.price.toFixed(2)}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayTrack(track);
                    }}
                    className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-purple-500"
                    data-testid={`play-track-${track.id}`}
                  >
                    {isCurrentTrackPlaying && isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4 ml-0.5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
